import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import { format } from "date-fns";
import { TrendUp, TrendDown, ArrowLeft, Percent, CaretUp, CaretDown, Funnel, Sun, Moon, Info, Lightning } from "phosphor-react";
import { Button } from "@/components/ui/button";
import GeniLoader from "../components/GeniLoader";

export default function PartialExecutionDetail() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [allTrades, setAllTrades] = useState([]);
  const [setups, setSetups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTrade, setSelectedTrade] = useState(null);
  
  // Read setup filter from URL query params
  const setupFilterFromUrl = searchParams.get('setup');
  
  // Filter and sort state
  const [directionFilter, setDirectionFilter] = useState(null);
  const [outcomeFilter, setOutcomeFilter] = useState(null); // null | 'win' | 'loss'
  const [tradeTypeFilter, setTradeTypeFilter] = useState(null); // null | 'day' | 'swing'
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState(null);

  // Timeframes ordered by duration for classification
  const TIMEFRAME_ORDER = ['1M', '5M', '15M', '30M', '1H', '2H', '4H', '6H', '12H', '1D', '1W'];
  const DAY_TRADE_MAX_INDEX = TIMEFRAME_ORDER.indexOf('2H'); // < 4H = Day Trade

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [tradesRes, setupsRes] = await Promise.all([
        api.get('/trades'),
        api.get('/setups')
      ]);
      setAllTrades(tradesRes.data);
      setSetups(setupsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load partial execution trades");
    } finally {
      setLoading(false);
    }
  };

  // Get setup name from setup_id
  const getSetupName = (setupId) => {
    if (!setupId) return null;
    const setup = setups.find(s => s.id === setupId);
    return setup?.name || null;
  };

  // Classify trade as Day or Swing based on timeframe
  const getTradeType = (timeframe) => {
    if (!timeframe) return 'day'; // Default to day if no timeframe
    const tfIndex = TIMEFRAME_ORDER.indexOf(timeframe.toUpperCase());
    if (tfIndex === -1) return 'day'; // Unknown timeframe defaults to day
    return tfIndex <= DAY_TRADE_MAX_INDEX ? 'day' : 'swing';
  };

  // Get trade type badge
  const getTradeTypeBadge = (timeframe) => {
    const type = getTradeType(timeframe);
    if (type === 'swing') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded bg-purple-500/10 text-purple-400 border border-purple-500/20">
          <Moon size={10} weight="fill" />
          Swing
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
        <Sun size={10} weight="fill" />
        Day
      </span>
    );
  };

  // Classify partial trades using EXACT same logic as AdvancedAnalytics
  const partialTradesData = useMemo(() => {
    // First apply setup filter if present
    let tradesToAnalyze = allTrades;
    
    if (setupFilterFromUrl) {
      if (setupFilterFromUrl === '__no_setup__') {
        tradesToAnalyze = allTrades.filter(t => !t.setup_id);
      } else {
        tradesToAnalyze = allTrades.filter(t => t.setup_id === setupFilterFromUrl);
      }
    }
    
    // Filter to only closed trades (WIN or LOSS) with valid profit and hand_size
    const closedTrades = tradesToAnalyze.filter(t => 
      t.status === 'closed' && 
      (t.outcome === 'win' || t.outcome === 'loss') &&
      t.profit_usd !== null && 
      t.profit_usd !== undefined &&
      t.hand_size !== null && 
      t.hand_size !== undefined &&
      t.hand_size > 0
    );

    // Classify partial trades: where abs(profit) < hand_size
    const partialTrades = closedTrades.filter(t => {
      const absProfit = Math.abs(t.profit_usd);
      // Allow small tolerance for floating point comparison (0.01)
      return absProfit < (t.hand_size - 0.01);
    });

    // Separate into reduced losses and partial profits
    const reducedLosses = partialTrades.filter(t => t.outcome === 'loss');
    const partialProfits = partialTrades.filter(t => t.outcome === 'win');

    // TRADE TYPE SEGMENTATION
    const dayTrades = partialTrades.filter(t => getTradeType(t.timeframe) === 'day');
    const swingTrades = partialTrades.filter(t => getTradeType(t.timeframe) === 'swing');

    // Day Trade Stats
    const dayReducedLosses = dayTrades.filter(t => t.outcome === 'loss').length;
    const dayPartialProfits = dayTrades.filter(t => t.outcome === 'win').length;
    const dayReducedLossRate = dayTrades.length > 0 ? Math.round((dayReducedLosses / dayTrades.length) * 100) : 0;
    const dayPartialProfitRate = dayTrades.length > 0 ? Math.round((dayPartialProfits / dayTrades.length) * 100) : 0;

    // Swing Trade Stats
    const swingReducedLosses = swingTrades.filter(t => t.outcome === 'loss').length;
    const swingPartialProfits = swingTrades.filter(t => t.outcome === 'win').length;
    const swingReducedLossRate = swingTrades.length > 0 ? Math.round((swingReducedLosses / swingTrades.length) * 100) : 0;
    const swingPartialProfitRate = swingTrades.length > 0 ? Math.round((swingPartialProfits / swingTrades.length) * 100) : 0;

    return {
      total: partialTrades.length,
      reducedLosses: reducedLosses.length,
      partialProfits: partialProfits.length,
      trades: partialTrades,
      // Day Trade Segment
      dayTrades: {
        total: dayTrades.length,
        reducedLosses: dayReducedLosses,
        partialProfits: dayPartialProfits,
        reducedLossRate: dayReducedLossRate,
        partialProfitRate: dayPartialProfitRate
      },
      // Swing Trade Segment
      swingTrades: {
        total: swingTrades.length,
        reducedLosses: swingReducedLosses,
        partialProfits: swingPartialProfits,
        reducedLossRate: swingReducedLossRate,
        partialProfitRate: swingPartialProfitRate
      }
    };
  }, [allTrades, setupFilterFromUrl]);

  // Generate Trade Type Insight for Partial Execution
  const tradeTypeInsight = useMemo(() => {
    const { dayTrades, swingTrades, total } = partialTradesData;
    
    // Need at least 3 trades total for any insight
    if (total < 3) return null;
    
    const hasEnoughDay = dayTrades.total >= 2;
    const hasEnoughSwing = swingTrades.total >= 2;
    
    if (!hasEnoughDay && !hasEnoughSwing) return null;
    
    // Generate insight based on comparison
    if (hasEnoughDay && hasEnoughSwing) {
      const dayMoreProtective = dayTrades.reducedLossRate > swingTrades.reducedLossRate;
      const swingMoreProtective = swingTrades.reducedLossRate > dayTrades.reducedLossRate;
      const dayMoreProfitable = dayTrades.partialProfitRate > swingTrades.partialProfitRate;
      const swingMoreProfitable = swingTrades.partialProfitRate > dayTrades.partialProfitRate;
      const significantDiff = Math.abs(dayTrades.reducedLossRate - swingTrades.reducedLossRate) >= 15 ||
                             Math.abs(dayTrades.partialProfitRate - swingTrades.partialProfitRate) >= 15;
      
      // Reduced loss comparison
      if (dayMoreProtective && significantDiff && dayTrades.reducedLossRate >= 40) {
        return {
          type: 'day-protective',
          text: `Partials are protecting losses more effectively in Day Trades (${dayTrades.reducedLossRate}% reduced loss rate) compared to Swing Trades (${swingTrades.reducedLossRate}%).`,
          color: '#00B8D4',
          icon: 'day'
        };
      }
      
      if (swingMoreProtective && significantDiff && swingTrades.reducedLossRate >= 40) {
        return {
          type: 'swing-protective',
          text: `Swing trades show a higher reduced-loss rate (${swingTrades.reducedLossRate}%) compared to Day Trades (${dayTrades.reducedLossRate}%). Partials may be more protective in longer timeframes.`,
          color: '#00B8D4',
          icon: 'swing'
        };
      }
      
      // Partial profit comparison
      if (dayMoreProfitable && significantDiff && dayTrades.partialProfitRate >= 40) {
        return {
          type: 'day-profitable',
          text: `Day Trades produce more partial profits (${dayTrades.partialProfitRate}%) than Swing Trades (${swingTrades.partialProfitRate}%). Partial execution performs better in intraday trades.`,
          color: '#00E676',
          icon: 'day'
        };
      }
      
      if (swingMoreProfitable && significantDiff && swingTrades.partialProfitRate >= 40) {
        return {
          type: 'swing-profitable',
          text: `Swing Trades produce more partial profits (${swingTrades.partialProfitRate}%) than Day Trades (${dayTrades.partialProfitRate}%). Partial execution may be more effective in longer positions.`,
          color: '#00E676',
          icon: 'swing'
        };
      }
      
      // General comparison when rates are similar
      if (!significantDiff) {
        return {
          type: 'balanced',
          text: `Partial execution shows similar effectiveness in both Day Trades and Swing Trades. Consider using partials in both trade types.`,
          color: '#9E9E9E',
          icon: 'info'
        };
      }
    } else if (hasEnoughDay && !hasEnoughSwing) {
      // Only enough day trade data
      if (dayTrades.reducedLossRate >= 40) {
        return {
          type: 'day-only',
          text: `Day Trades show strong loss protection through partials (${dayTrades.reducedLossRate}% reduced loss rate). Not enough Swing Trade data yet.`,
          color: '#00B8D4',
          icon: 'day'
        };
      } else if (dayTrades.partialProfitRate >= 40) {
        return {
          type: 'day-only',
          text: `Day Trades show good partial profit rate (${dayTrades.partialProfitRate}%). Not enough Swing Trade data yet.`,
          color: '#00E676',
          icon: 'day'
        };
      }
    } else if (hasEnoughSwing && !hasEnoughDay) {
      // Only enough swing trade data
      if (swingTrades.reducedLossRate >= 40) {
        return {
          type: 'swing-only',
          text: `Swing Trades show strong loss protection through partials (${swingTrades.reducedLossRate}% reduced loss rate). Not enough Day Trade data yet.`,
          color: '#00B8D4',
          icon: 'swing'
        };
      } else if (swingTrades.partialProfitRate >= 40) {
        return {
          type: 'swing-only',
          text: `Swing Trades show good partial profit rate (${swingTrades.partialProfitRate}%). Not enough Day Trade data yet.`,
          color: '#00E676',
          icon: 'swing'
        };
      }
    }
    
    return null;
  }, [partialTradesData]);

  // Calculate percentage of all trades
  const percentOfAll = useMemo(() => {
    let totalTradesCount = allTrades.length;
    
    if (setupFilterFromUrl) {
      if (setupFilterFromUrl === '__no_setup__') {
        totalTradesCount = allTrades.filter(t => !t.setup_id).length;
      } else {
        totalTradesCount = allTrades.filter(t => t.setup_id === setupFilterFromUrl).length;
      }
    }
    
    if (totalTradesCount === 0) return 0;
    return ((partialTradesData.total / totalTradesCount) * 100).toFixed(1);
  }, [allTrades, partialTradesData.total, setupFilterFromUrl]);

  // Clear setup filter
  const clearSetupFilter = () => {
    setSearchParams({});
  };

  // Cycle direction filter
  const cycleDirectionFilter = () => {
    if (directionFilter === null) setDirectionFilter('long');
    else if (directionFilter === 'long') setDirectionFilter('short');
    else setDirectionFilter(null);
  };

  // Cycle outcome filter
  const cycleOutcomeFilter = () => {
    if (outcomeFilter === null) setOutcomeFilter('win');
    else if (outcomeFilter === 'win') setOutcomeFilter('loss');
    else setOutcomeFilter(null);
  };

  // Cycle Date sort
  const cycleDateSort = () => {
    if (sortColumn !== 'date') {
      setSortColumn('date');
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  // Cycle RR sort
  const cycleRRSort = () => {
    if (sortColumn !== 'rr') {
      setSortColumn('rr');
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  // Cycle Profit sort
  const cycleProfitSort = () => {
    if (sortColumn !== 'profit') {
      setSortColumn('profit');
      setSortDirection('desc');
    } else if (sortDirection === 'desc') {
      setSortDirection('asc');
    } else {
      setSortColumn(null);
      setSortDirection(null);
    }
  };

  // Cycle trade type filter
  const cycleTradeTypeFilter = () => {
    if (tradeTypeFilter === null) setTradeTypeFilter('day');
    else if (tradeTypeFilter === 'day') setTradeTypeFilter('swing');
    else setTradeTypeFilter(null);
  };

  // Apply filters and sorting
  const filteredAndSortedTrades = useMemo(() => {
    let result = [...partialTradesData.trades];

    // Apply direction filter
    if (directionFilter) {
      result = result.filter(t => t.direction === directionFilter);
    }

    // Apply outcome filter
    if (outcomeFilter) {
      result = result.filter(t => t.outcome === outcomeFilter);
    }

    // Apply trade type filter
    if (tradeTypeFilter) {
      result = result.filter(t => getTradeType(t.timeframe) === tradeTypeFilter);
    }

    // Apply sorting
    if (sortColumn && sortDirection) {
      result.sort((a, b) => {
        let aVal, bVal;
        
        if (sortColumn === 'date') {
          aVal = new Date(a.created_at).getTime();
          bVal = new Date(b.created_at).getTime();
        } else if (sortColumn === 'rr') {
          aVal = a.rr ?? 0;
          bVal = b.rr ?? 0;
        } else if (sortColumn === 'profit') {
          aVal = a.profit_usd ?? 0;
          bVal = b.profit_usd ?? 0;
        }
        
        if (sortDirection === 'desc') {
          return bVal - aVal;
        } else {
          return aVal - bVal;
        }
      });
    } else {
      // Default sort by date descending
      result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    }

    return result;
  }, [partialTradesData.trades, directionFilter, outcomeFilter, tradeTypeFilter, sortColumn, sortDirection]);

  // Column header component with filter/sort indicator
  const ColumnHeader = ({ label, isActive, activeLabel, onClick, sortDir }) => (
    <th 
      className={`p-3 cursor-pointer select-none transition-colors hover:bg-white/5 ${
        isActive ? 'text-white' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-center gap-1.5">
        <span>{label}</span>
        {isActive && (
          <span className="flex items-center">
            {sortDir === 'desc' && <CaretDown size={12} weight="bold" className="text-[#00B8D4]" />}
            {sortDir === 'asc' && <CaretUp size={12} weight="bold" className="text-[#00B8D4]" />}
            {!sortDir && activeLabel && (
              <span className="text-[10px] px-1 py-0.5 rounded bg-[#00B8D4]/20 text-[#00B8D4] font-semibold ml-1">
                {activeLabel}
              </span>
            )}
          </span>
        )}
      </div>
    </th>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
        <GeniLoader size="md" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="partial-execution-detail-page">
      <div className="w-full max-w-[1600px] mx-auto px-4 md:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <Button
            onClick={() => navigate('/advanced')}
            variant="ghost"
            className="text-white/60 hover:text-white hover:bg-white/5 p-2"
            data-testid="back-to-advanced"
          >
            <ArrowLeft size={20} weight="bold" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <Percent size={28} weight="duotone" className="text-[#00B8D4]" />
              <div>
                <div className="text-white/40 text-xs font-mono uppercase tracking-wider">ANALYTICS</div>
                <h2 className="text-3xl font-black font-heading tracking-tight" data-testid="page-title">
                  Partial Execution
                </h2>
              </div>
            </div>
          </div>
          <div className="bg-[#121212] border border-white/10 rounded-sm px-4 py-2">
            <span className="text-white/60 text-sm font-mono">TOTAL: </span>
            <span className="text-[#00B8D4] font-bold text-lg" data-testid="total-count">{partialTradesData.total}</span>
          </div>
        </div>

        {/* Setup Filter Indicator */}
        {setupFilterFromUrl && (
          <div className="mb-4 flex items-center gap-2 text-white/60 text-sm" data-testid="setup-filter-indicator">
            <span>Showing partial trades for:</span>
            <span className="font-bold text-[#00B8D4]">
              {setupFilterFromUrl === '__no_setup__' ? 'No setup' : (getSetupName(setupFilterFromUrl) || setupFilterFromUrl)}
            </span>
            <button
              onClick={clearSetupFilter}
              className="text-white/40 hover:text-white/60 ml-1 underline"
              data-testid="clear-setup-filter"
            >
              (clear)
            </button>
          </div>
        )}

        {/* Performance Summary Row */}
        {partialTradesData.total > 0 && (
          <div className="mb-6" data-testid="partial-execution-summary">
            <div className="text-white/40 text-xs font-mono uppercase tracking-wider mb-3">
              Summary
            </div>
            <div className="bg-[#121212] border border-white/10 rounded-sm p-5">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Total Trades */}
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">TRADES</div>
                  <div className="text-xl font-bold font-mono text-[#00B8D4]" data-testid="summary-total">
                    {partialTradesData.total}
                  </div>
                </div>
                
                {/* % of Total */}
                <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">% OF ALL TRADES</div>
                  <div className="text-xl font-bold font-mono text-white" data-testid="summary-percent">
                    {percentOfAll}%
                  </div>
                </div>
                
                {/* Reduced Losses */}
                <div className="bg-[#000000] border border-[#FF3B30]/20 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">REDUCED LOSSES</div>
                  <div className="text-xl font-bold font-mono text-[#FF3B30]" data-testid="summary-reduced-losses">
                    {partialTradesData.reducedLosses}
                  </div>
                </div>
                
                {/* Partial Profits */}
                <div className="bg-[#000000] border border-[#00E676]/20 rounded-sm p-3">
                  <div className="text-white/40 text-xs font-mono mb-1">PARTIAL PROFITS</div>
                  <div className="text-xl font-bold font-mono text-[#00E676]" data-testid="summary-partial-profits">
                    {partialTradesData.partialProfits}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TRADE TYPE SEGMENTATION - Day vs Swing */}
        {partialTradesData.total > 0 && (
          <div className="mb-6" data-testid="trade-type-segmentation">
            <div className="text-white/40 text-xs font-mono uppercase tracking-wider mb-3">
              Partial Performance by Trade Type
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Day Trade Performance */}
              <div className="bg-[#121212] border border-blue-500/20 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Sun size={18} weight="duotone" className="text-blue-400" />
                  <span className="text-blue-400 text-sm font-bold">Day Trade</span>
                </div>
                {partialTradesData.dayTrades.total === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-white/40 text-sm">No partial data for Day Trades yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-[#0A0A0A] rounded p-2 text-center">
                        <div className="text-white/40 text-[10px] font-mono">TRADES</div>
                        <div className="text-lg font-bold font-mono text-white">{partialTradesData.dayTrades.total}</div>
                      </div>
                      <div className="bg-[#0A0A0A] rounded p-2 text-center">
                        <div className="text-white/40 text-[10px] font-mono">REDUCED LOSSES</div>
                        <div className="text-lg font-bold font-mono text-[#FF3B30]">{partialTradesData.dayTrades.reducedLosses}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-[#0A0A0A] rounded p-2 text-center">
                        <div className="text-white/40 text-[10px] font-mono">PARTIAL PROFITS</div>
                        <div className="text-lg font-bold font-mono text-[#00E676]">{partialTradesData.dayTrades.partialProfits}</div>
                      </div>
                      <div className="bg-[#0A0A0A] rounded p-2 text-center">
                        <div className="text-white/40 text-[10px] font-mono">REDUCED LOSS RATE</div>
                        <div className="text-lg font-bold font-mono text-[#00B8D4]">{partialTradesData.dayTrades.reducedLossRate}%</div>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-white/10 text-center">
                      <span className="text-white/40 text-xs">Partial Profit Rate: </span>
                      <span className={`font-mono font-bold ${
                        partialTradesData.dayTrades.partialProfitRate >= 50 ? 'text-[#00E676]' : 'text-white/60'
                      }`}>{partialTradesData.dayTrades.partialProfitRate}%</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Swing Trade Performance */}
              <div className="bg-[#121212] border border-purple-500/20 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Moon size={18} weight="duotone" className="text-purple-400" />
                  <span className="text-purple-400 text-sm font-bold">Swing Trade</span>
                </div>
                {partialTradesData.swingTrades.total === 0 ? (
                  <div className="text-center py-4">
                    <p className="text-white/40 text-sm">No partial data for Swing Trades yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-[#0A0A0A] rounded p-2 text-center">
                        <div className="text-white/40 text-[10px] font-mono">TRADES</div>
                        <div className="text-lg font-bold font-mono text-white">{partialTradesData.swingTrades.total}</div>
                      </div>
                      <div className="bg-[#0A0A0A] rounded p-2 text-center">
                        <div className="text-white/40 text-[10px] font-mono">REDUCED LOSSES</div>
                        <div className="text-lg font-bold font-mono text-[#FF3B30]">{partialTradesData.swingTrades.reducedLosses}</div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div className="bg-[#0A0A0A] rounded p-2 text-center">
                        <div className="text-white/40 text-[10px] font-mono">PARTIAL PROFITS</div>
                        <div className="text-lg font-bold font-mono text-[#00E676]">{partialTradesData.swingTrades.partialProfits}</div>
                      </div>
                      <div className="bg-[#0A0A0A] rounded p-2 text-center">
                        <div className="text-white/40 text-[10px] font-mono">REDUCED LOSS RATE</div>
                        <div className="text-lg font-bold font-mono text-[#00B8D4]">{partialTradesData.swingTrades.reducedLossRate}%</div>
                      </div>
                    </div>
                    <div className="pt-2 border-t border-white/10 text-center">
                      <span className="text-white/40 text-xs">Partial Profit Rate: </span>
                      <span className={`font-mono font-bold ${
                        partialTradesData.swingTrades.partialProfitRate >= 50 ? 'text-[#00E676]' : 'text-white/60'
                      }`}>{partialTradesData.swingTrades.partialProfitRate}%</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Trade Type Insight */}
            {tradeTypeInsight && (
              <div 
                className="mt-4 p-4 rounded-sm border flex items-start gap-3"
                style={{
                  backgroundColor: `${tradeTypeInsight.color}10`,
                  borderColor: `${tradeTypeInsight.color}30`
                }}
                data-testid="trade-type-insight"
              >
                {tradeTypeInsight.icon === 'day' && <Sun size={20} weight="duotone" style={{ color: tradeTypeInsight.color }} />}
                {tradeTypeInsight.icon === 'swing' && <Moon size={20} weight="duotone" style={{ color: tradeTypeInsight.color }} />}
                {tradeTypeInsight.icon === 'info' && <Info size={20} weight="duotone" style={{ color: tradeTypeInsight.color }} />}
                {tradeTypeInsight.icon === 'warning' && <Lightning size={20} weight="duotone" style={{ color: tradeTypeInsight.color }} />}
                <p className="text-sm" style={{ color: tradeTypeInsight.color }}>
                  {tradeTypeInsight.text}
                </p>
              </div>
            )}
          </div>
        )}

        {/* Content */}
        {partialTradesData.total === 0 ? (
          <div className="bg-[#121212] border border-white/10 rounded-sm p-12 text-center">
            <div className="w-16 h-16 bg-[#00B8D4]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Percent size={32} weight="duotone" className="text-[#00B8D4]/60" />
            </div>
            <h3 className="text-xl font-bold font-heading mb-2">
              {setupFilterFromUrl ? "No Partial Trades for This Setup" : "No Partial Trades Found"}
            </h3>
            <p className="text-white/60 mb-4">
              {setupFilterFromUrl 
                ? "No trades closed before full TP/SL for this setup."
                : "Trades closed before full TP/SL will appear here."}
            </p>
            {setupFilterFromUrl && (
              <Button
                onClick={clearSetupFilter}
                variant="outline"
                className="border-white/10 text-white/70 hover:bg-white/5"
                data-testid="clear-filter-empty-state"
              >
                View All Partial Trades
              </Button>
            )}
          </div>
        ) : (
          <div className="flex gap-6">
            {/* Trade List */}
            <div className="flex-1">
              {/* Active Filters Summary */}
              {(directionFilter || outcomeFilter || tradeTypeFilter || sortColumn) && (
                <div className="mb-3 flex items-center gap-2 text-xs">
                  <Funnel size={14} className="text-white/40" />
                  <span className="text-white/40">Active:</span>
                  {directionFilter && (
                    <span className="px-2 py-0.5 rounded bg-white/10 text-white/80">
                      {directionFilter.toUpperCase()}
                    </span>
                  )}
                  {tradeTypeFilter && (
                    <span className={`px-2 py-0.5 rounded ${
                      tradeTypeFilter === 'day' ? 'bg-blue-500/20 text-blue-400' : 'bg-purple-500/20 text-purple-400'
                    }`}>
                      {tradeTypeFilter === 'day' ? 'Day' : 'Swing'}
                    </span>
                  )}
                  {outcomeFilter && (
                    <span className={`px-2 py-0.5 rounded ${
                      outcomeFilter === 'win' ? 'bg-[#00E676]/20 text-[#00E676]' : 'bg-[#FF3B30]/20 text-[#FF3B30]'
                    }`}>
                      {outcomeFilter === 'win' ? 'Partial Profit' : 'Reduced Loss'}
                    </span>
                  )}
                  {sortColumn && (
                    <span className="px-2 py-0.5 rounded bg-white/10 text-white/80">
                      {sortColumn.toUpperCase()} {sortDirection === 'desc' ? '↓' : '↑'}
                    </span>
                  )}
                  <button 
                    onClick={() => {
                      setDirectionFilter(null);
                      setOutcomeFilter(null);
                      setTradeTypeFilter(null);
                      setSortColumn(null);
                      setSortDirection(null);
                    }}
                    className="text-white/40 hover:text-white ml-2 underline"
                    data-testid="clear-all-filters"
                  >
                    Clear all
                  </button>
                </div>
              )}
              
              <div className="bg-[#121212] border border-white/10 rounded-sm overflow-hidden">
                <table className="w-full" data-testid="partial-trades-table">
                  <thead className="bg-[#0A0A0A]">
                    <tr className="text-left text-xs text-white/40 font-mono">
                      <th className="p-3">ASSET</th>
                      <ColumnHeader 
                        label="DIRECTION" 
                        isActive={!!directionFilter}
                        activeLabel={directionFilter?.toUpperCase()}
                        onClick={cycleDirectionFilter}
                      />
                      <ColumnHeader 
                        label="TYPE" 
                        isActive={!!tradeTypeFilter}
                        activeLabel={tradeTypeFilter === 'day' ? 'DAY' : tradeTypeFilter === 'swing' ? 'SWING' : null}
                        onClick={cycleTradeTypeFilter}
                      />
                      <ColumnHeader 
                        label="OUTCOME" 
                        isActive={!!outcomeFilter}
                        activeLabel={outcomeFilter === 'win' ? 'PROFIT' : outcomeFilter === 'loss' ? 'LOSS' : null}
                        onClick={cycleOutcomeFilter}
                      />
                      <ColumnHeader 
                        label="RR" 
                        isActive={sortColumn === 'rr'}
                        sortDir={sortColumn === 'rr' ? sortDirection : null}
                        onClick={cycleRRSort}
                      />
                      <ColumnHeader 
                        label="PROFIT" 
                        isActive={sortColumn === 'profit'}
                        sortDir={sortColumn === 'profit' ? sortDirection : null}
                        onClick={cycleProfitSort}
                      />
                      <ColumnHeader 
                        label="DATE" 
                        isActive={sortColumn === 'date'}
                        sortDir={sortColumn === 'date' ? sortDirection : null}
                        onClick={cycleDateSort}
                      />
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAndSortedTrades.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="p-8 text-center text-white/40">
                          No trades match current filters
                        </td>
                      </tr>
                    ) : (
                      filteredAndSortedTrades.map((trade) => (
                        <tr
                          key={trade.id}
                          data-testid={`trade-row-${trade.id}`}
                          onClick={() => setSelectedTrade(trade)}
                          className={`trade-row border-t border-white/10 cursor-pointer hover:bg-white/5 ${
                            selectedTrade?.id === trade.id ? "bg-white/5" : ""
                          }`}
                        >
                          <td className="p-3">
                            <div className="font-mono text-white font-bold">{trade.asset}</div>
                            <div className="text-xs text-white/40">{trade.timeframe || '-'}</div>
                          </td>
                          <td className="p-3">
                            <div className="flex items-center gap-2">
                              {trade.direction === "long" ? (
                                <TrendUp size={16} weight="bold" className="text-[#00E676]" />
                              ) : (
                                <TrendDown size={16} weight="bold" className="text-[#FF3B30]" />
                              )}
                              <span
                                className={`font-mono text-sm uppercase ${
                                  trade.direction === "long" ? "text-[#00E676]" : "text-[#FF3B30]"
                                }`}
                              >
                                {trade.direction}
                              </span>
                            </div>
                          </td>
                          <td className="p-3">
                            {getTradeTypeBadge(trade.timeframe)}
                          </td>
                          <td className="p-3">
                            <span className={`px-2 py-0.5 text-xs font-mono rounded ${
                              trade.outcome === 'win' 
                                ? 'bg-[#00E676]/10 text-[#00E676]' 
                                : 'bg-[#FF3B30]/10 text-[#FF3B30]'
                            }`}>
                              {trade.outcome === 'win' ? 'Partial Profit' : 'Reduced Loss'}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className={`font-mono text-sm ${
                              (trade.rr ?? 0) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                            }`}>
                              {trade.rr != null ? trade.rr.toFixed(2) : '-'}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className={`font-mono text-sm ${
                              (trade.profit_usd ?? 0) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                            }`}>
                              {trade.profit_usd != null ? `$${trade.profit_usd.toFixed(2)}` : '-'}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className="text-white/60 text-sm">
                              {trade.created_at && format(new Date(trade.created_at), "MMM d, yyyy")}
                            </span>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                
                {/* Results count */}
                {(directionFilter || outcomeFilter || tradeTypeFilter) && (
                  <div className="p-3 border-t border-white/10 text-xs text-white/40">
                    Showing {filteredAndSortedTrades.length} of {partialTradesData.total} trades
                  </div>
                )}
              </div>
            </div>

            {/* Trade Details Panel */}
            {selectedTrade && (
              <div className="w-[400px] shrink-0">
                <div className="bg-[#121212] border border-white/10 rounded-sm p-6 sticky top-8" data-testid="trade-details-panel">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold font-heading">Trade Details</h3>
                    <Button
                      onClick={() => setSelectedTrade(null)}
                      variant="ghost"
                      size="sm"
                      className="text-white/40 hover:text-white hover:bg-white/5"
                      data-testid="close-details-panel"
                    >
                      <ArrowLeft size={16} />
                    </Button>
                  </div>

                  <div className="space-y-6">
                    {/* Chart Image */}
                    {selectedTrade.image_data && (
                      <div className="bg-[#000000] border border-white/10 rounded-sm overflow-hidden">
                        <img 
                          src={`data:image/png;base64,${selectedTrade.image_data}`}
                          alt="Trade chart"
                          className="w-full h-auto object-contain"
                          data-testid="trade-chart-image"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                    )}

                    {/* Asset & Direction */}
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-3xl font-black font-heading">{selectedTrade.asset}</div>
                        <div className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded text-xs font-bold ${
                          selectedTrade.direction === "long" 
                            ? "bg-[#00E676]/10 text-[#00E676]" 
                            : "bg-[#FF3B30]/10 text-[#FF3B30]"
                        }`}>
                          {selectedTrade.direction === "long" ? (
                            <TrendUp size={14} weight="bold" />
                          ) : (
                            <TrendDown size={14} weight="bold" />
                          )}
                          {selectedTrade.direction?.toUpperCase()}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-white/40 mb-1">OUTCOME</div>
                        <div className={`px-2 py-1 rounded text-sm font-bold ${
                          selectedTrade.outcome === 'win'
                            ? 'bg-[#00E676]/10 text-[#00E676]'
                            : 'bg-[#FF3B30]/10 text-[#FF3B30]'
                        }`}>
                          {selectedTrade.outcome === 'win' ? 'PARTIAL PROFIT' : 'REDUCED LOSS'}
                        </div>
                      </div>
                    </div>

                    {/* Partial Execution Info */}
                    <div className="bg-[#00B8D4]/5 border border-[#00B8D4]/20 rounded-sm p-4">
                      <div className="text-white/40 text-xs font-mono mb-2">PARTIAL EXECUTION</div>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <span className="text-white/40">Hand Size:</span>
                          <span className="text-white ml-2 font-mono">${selectedTrade.hand_size?.toFixed(2)}</span>
                        </div>
                        <div>
                          <span className="text-white/40">Actual P/L:</span>
                          <span className={`ml-2 font-mono ${
                            (selectedTrade.profit_usd ?? 0) >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                          }`}>
                            ${selectedTrade.profit_usd?.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Price Levels */}
                    <div className="grid grid-cols-2 gap-4">
                      {selectedTrade.entry && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">ENTRY</div>
                          <div className="text-white font-mono font-semibold">
                            ${selectedTrade.entry?.toLocaleString()}
                          </div>
                        </div>
                      )}
                      {selectedTrade.exit && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">EXIT</div>
                          <div className={`font-mono font-semibold ${
                            selectedTrade.outcome === 'win' ? 'text-[#00E676]' : 'text-[#FF3B30]'
                          }`}>
                            ${selectedTrade.exit?.toLocaleString()}
                          </div>
                        </div>
                      )}
                      {selectedTrade.stop_loss && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">STOP LOSS</div>
                          <div className="text-[#FF3B30] font-mono font-semibold">
                            ${selectedTrade.stop_loss?.toLocaleString()}
                          </div>
                        </div>
                      )}
                      {selectedTrade.take_profit && (
                        <div className="bg-[#000000] border border-white/10 rounded-sm p-3">
                          <div className="text-white/40 text-xs font-mono mb-1">TAKE PROFIT</div>
                          <div className="text-[#00E676] font-mono font-semibold">
                            ${selectedTrade.take_profit?.toLocaleString()}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Additional Info */}
                    <div className="space-y-3 pt-4 border-t border-white/10">
                      {selectedTrade.rr != null && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white/40">Realized RR</span>
                          <span className={`font-mono ${
                            selectedTrade.rr >= 0 ? 'text-[#00E676]' : 'text-[#FF3B30]'
                          }`}>
                            {selectedTrade.rr.toFixed(2)}
                          </span>
                        </div>
                      )}
                      {selectedTrade.timeframe && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white/40">Timeframe</span>
                          <span className="text-white font-mono">{selectedTrade.timeframe}</span>
                        </div>
                      )}
                      {selectedTrade.setup_name && (
                        <div className="flex justify-between text-sm">
                          <span className="text-white/40">Setup</span>
                          <span className="text-white font-medium">{selectedTrade.setup_name}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-sm">
                        <span className="text-white/40">Closed</span>
                        <span className="text-white/60">
                          {selectedTrade.closed_at && format(new Date(selectedTrade.closed_at), "MMM d, yyyy HH:mm")}
                        </span>
                      </div>
                    </div>

                    {/* Notes */}
                    {selectedTrade.notes && (
                      <div className="pt-4 border-t border-white/10">
                        <div className="text-white/40 text-xs font-mono mb-2">NOTES</div>
                        <p className="text-white/80 text-sm whitespace-pre-wrap">{selectedTrade.notes}</p>
                      </div>
                    )}

                    {/* Read-only notice */}
                    <div className="pt-4 border-t border-white/10">
                      <p className="text-white/30 text-xs text-center">
                        This is a read-only view. To edit trades, go to Trade History.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
